import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import * as React from 'react';
import { css, html } from 'react-strict-dom';

import { Text } from '../../../components';
import { Style } from '../../../constants';
import { color } from '../../../constants/color.css';
import { type WishlistColumn } from '../board';

type Props = {
  column: WishlistColumn;
  label: string;
  count: number;
  emptyCopy: string;
  empty: boolean;
  sortableIds: ReadonlyArray<string>;
  children: React.ReactNode;
  style?: css.StyleXStyles;
};

export default function BoardColumn({
  column,
  label,
  count,
  emptyCopy,
  empty,
  sortableIds,
  children,
  style,
}: Props) {
  const { setNodeRef } = useDroppable({ id: column });

  return (
    <html.div ref={setNodeRef} style={[styles.panel, style]}>
      <html.div style={[Style.row, Style.alignCenter, styles.header]}>
        <Text typeface="body.medium" color="neutral100">
          {label}
        </Text>
        <html.span style={styles.countChip}>
          <Text typeface="caption.medium" color="neutral70">
            {count}
          </Text>
        </html.span>
      </html.div>

      <SortableContext
        items={[...sortableIds]}
        strategy={verticalListSortingStrategy}
      >
        <html.div style={styles.cards}>
          {children}
          {empty && (
            <html.div style={styles.empty}>
              <Text typeface="body.small.regular" color="neutral40">
                {emptyCopy}
              </Text>
            </html.div>
          )}
        </html.div>
      </SortableContext>
    </html.div>
  );
}

const styles = css.create({
  panel: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    width: {
      default: 282,
      '@media (max-width: 768px)': '100%',
    },
    minWidth: {
      default: 282,
      '@media (max-width: 768px)': 0,
    },
    flexShrink: 0,
    minHeight: 0,
    padding: 12,
    backgroundColor: color.neutral10,
    borderRadius: 12,
  },
  header: {
    gap: 8,
    minHeight: 24,
  },
  countChip: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 24,
    height: 21,
    paddingInline: 6,
    backgroundColor: color.neutral20,
    borderRadius: 8,
  },
  cards: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    minHeight: 0,
  },
  empty: {
    paddingBlock: 16,
    textAlign: 'center',
  },
});
